import { useEffect } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { House, AddressBook, Package, CreditCard, ShieldStar, SignOut, Warning, ChartBar, Receipt, ChatCircleText } from "@phosphor-icons/react";
import { Logo } from "@/components/common";
import { useAuth } from "@/lib/auth";
import { fmtDate, usd } from "@/lib/api";

const NAV = [
  ["/dashboard", House, "My Airbnbs", "nav-properties"],
  ["/dashboard/contacts", AddressBook, "Guest contacts", "nav-contacts"],
  ["/dashboard/insights", ChartBar, "Insights", "nav-insights"],
  ["/dashboard/documents", Receipt, "Invoices & receipts", "nav-documents"],
  ["/dashboard/orders", Package, "Order cards", "nav-orders"],
  ["/dashboard/subscription", CreditCard, "Subscription", "nav-subscription"],
  ["/dashboard/messages", ChatCircleText, "Message Hostkey", "nav-messages"],
];

function SubBanner({ sub }) {
  if (!sub || sub.status === "owner" || sub.status === "active") return null;
  const map = {
    trial: ["bg-[#FBEBD2] text-[#6B4510]", `Free trial: ${sub.trial_days_left} day${sub.trial_days_left === 1 ? "" : "s"} left. After that it's ${usd(sub.monthly_fee || sub.price)}/month.`],
    pastdue: ["bg-[#F9DED4] text-[#8A3417]", `Payment due. Your guest pages stay live until ${fmtDate(sub.grace_until)}.`],
    expired: ["bg-[#F4D4D4] text-[#7A1F1F]", "Your guest pages are paused. Pay to switch them back on."],
    suspended: ["bg-[#E8E4DF] text-[#4A4440]", "Your account is suspended. Please contact Hostkey."],
  };
  const [cls, msg] = map[sub.status];
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 px-6 py-2.5 text-sm ${cls}`} data-testid="subscription-banner">
      <span className="flex items-center gap-2"><Warning size={16} weight="bold" /> {msg}</span>
      <Link to="/dashboard/subscription" className="font-semibold underline underline-offset-4" data-testid="subscription-banner-link">How to pay</Link>
    </div>
  );
}

export default function HostLayout() {
  const { user, logout, refresh } = useAuth();
  useEffect(() => { refresh(); }, [refresh]);
  const link = ({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#151312] text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;
  return (
    <div className="min-h-screen lg:flex" data-testid="host-layout">
      <aside className="border-b border-border bg-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:flex-col lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-5 py-4 lg:py-6"><Link to="/"><Logo /></Link>
          <button onClick={logout} className="text-muted-foreground lg:hidden" data-testid="mobile-logout-button"><SignOut size={20} /></button></div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 no-scrollbar lg:flex-col lg:pb-0">
          {NAV.map(([to, Icon, label, tid]) => <NavLink key={to} to={to} end={to === "/dashboard"} className={link} data-testid={tid}><Icon size={18} /> <span className="whitespace-nowrap">{label}</span></NavLink>)}
          {["owner", "admin"].includes(user?.role) && <NavLink to="/admin" className={link} data-testid="nav-admin"><ShieldStar size={18} /> <span className="whitespace-nowrap">Owner dashboard</span></NavLink>}
        </nav>
        <div className="mt-auto hidden border-t border-border p-4 lg:block">
          <div className="flex items-center gap-3">
            {user?.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full" /> : <div className="h-9 w-9 rounded-full bg-secondary" />}
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</p><p className="truncate text-xs text-muted-foreground">{user?.email}</p></div>
            <button onClick={logout} data-testid="logout-button" className="text-muted-foreground hover:text-foreground"><SignOut size={18} /></button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <SubBanner sub={user?.subscription} />
        <div className="mx-auto max-w-7xl p-5 sm:p-8 lg:p-10"><Outlet /></div>
      </main>
    </div>
  );
}
